import { useTranslation } from "react-i18next";
import { KitchenHoldAllChip } from "./KitchenHoldAll";
import { OrderPanelContent } from "./OrderPanelContent";
import { PreviewFilterChips, type PreviewFilter } from "./PreviewFilterChips";
import type { ColorBy } from "./catalogColors";
import { SIDE_PANEL_WIDTH_CLASS } from "../sidePanelWidth";
import type { KitchenHoldControls } from "./useKitchenHoldChoice";

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
  comment?: string;
}

interface OrderPanelProps {
  orderItems: OrderItem[];
  comment: string;
  showCommentInput: boolean;
  onCommentChange: (comment: string) => void;
  onToggleComment: () => void;
  onQuantityChange: (index: number, delta: number) => void;
  /** Отваря модала за редакция на модификатори/коментар от молива на реда (виж OrderPanelContent). */
  onItemEdit: (indices: number[], orderItem: OrderItem) => void;
  onSubmit: () => void;
  submitting: boolean;
  /** Клиент с отстъпка/надбавка по сметката — виж OrderPanelContent. */
  billDiscount?: { percent: number; clientName: string | null } | null;
  /** Кое стои над прегледа — другото спрямо реда над каталога. Кликът
   *  ФИЛТРИРА КАТАЛОГА (станция или група), не редовете на прегледа. */
  previewFilterKind: "station" | "group";
  previewFilter: PreviewFilter;
  onPreviewFilterChange: (value: PreviewFilter) => void;
  /** Групите на дневни менюта без видим продукт — без чип над прегледа */
  hiddenCategoryIds?: ReadonlySet<number>;
  colorBy: ColorBy;
  /** Времето за кухнята — виж OrderPanelContent; хапчето е в заглавието. */
  kitchenHold?: KitchenHoldControls;
}

/**
 * Десктоп панел „Преглед на поръчката" — постоянно видим вдясно от продуктовия
 * списък (замества стария отделен summary екран). Тялото е споделеното
 * OrderPanelContent; тук е само обвивката с хедъра и брояча на редовете.
 */
export const OrderPanel = (props: OrderPanelProps) => {
  const { t } = useTranslation();

  return (
    <aside className={`${SIDE_PANEL_WIDTH_CLASS} shrink-0 border-l border-pos-line bg-pos-panel flex flex-col min-h-0 gap-2.5 p-2.5`}>
      {/* 1) Филтърът (станции или групи) — самостоятелна карта над прегледа */}
      <div className="shrink-0 rounded-[14px] border border-pos-line3 bg-pos-card">
        <PreviewFilterChips
          kind={props.previewFilterKind}
          value={props.previewFilter}
          onChange={props.onPreviewFilterChange}
          hiddenCategoryIds={props.hiddenCategoryIds}
        />
      </div>

      {/* 2) „Преглед на поръчката" — отделна карта: хедър + редове + футър */}
      <div className="flex-1 min-h-0 flex flex-col rounded-[14px] border border-pos-line3 bg-pos-card overflow-hidden">
        {/* Хедър като на горната карта: kicker + времето за кухнята на всички
            редове (без него — брой артикули; масата е във футъра) */}
        <div className="px-3 pt-3 pb-1 flex items-center justify-between gap-2">
          <span className="font-pos-mono text-[13px] leading-[16.5px] font-semibold tracking-[.1em] uppercase text-pos-sec-head">
            {t("ordering.orderPreview")}
          </span>
          {props.kitchenHold ? (
            <KitchenHoldAllChip controls={props.kitchenHold} />
          ) : (
            <span className="font-pos-mono text-[13px] leading-[16.5px] font-semibold text-pos-muted">
              {t("ordering.itemsShortCount", { count: props.orderItems.length })}
            </span>
          )}
        </div>
        <OrderPanelContent {...props} variant="panel" />
      </div>
    </aside>
  );
};
